import { Fragment, useState } from 'react';
import { money, num } from '../pages/Dashboard';

/** What the client table reads from an order row (the flow queue's row). */
export interface ClientRow {
  poId: string;
  poNumber?: string | null;
  poDate?: string | null;
  client: string | null;
  orderType?: string | null;
  productName?: string | null;
  jobName?: string | null;
  qty?: string | null;
  rate?: string | null;
  jobId?: string | number | null;
  jcDate?: string | null;
  promiseDate?: string | null;
  jcPromiseDate?: string | null;
}
export type Stage = 'all' | 'awaiting_jc' | 'has_jc' | 'awaiting_dod';

const n = (v: number) => v.toLocaleString('en-IN');
const day = (v: string | null | undefined) =>
  (v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '');
const jcNo = (id: string | number) => String(id).split('/').pop();

/**
 * Client by client: how many orders, how many have a job card and how many
 * are still waiting for one.  Click a client for its orders - PO, product,
 * job card, quantity, boxes left and DOD.
 */
export function ClientSummary<T extends ClientRow>({ rows, balanceOf, onOpenPo, maxHeight = 'max-h-[72vh]' }: {
  rows: T[];
  /** Boxes still to send on an order. */
  balanceOf: (r: T) => number;
  /** Open one order (its PO). */
  onOpenPo?: (poId: string) => void;
  maxHeight?: string;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  type Agg = { client: string; orders: T[]; jc: number; noJc: number; noDod: number; left: number; value: number };
  const by = new Map<string, Agg>();
  for (const r of rows) {
    const client = (r.client ?? '').trim() || '(no client)';
    const a = by.get(client) ?? { client, orders: [], jc: 0, noJc: 0, noDod: 0, left: 0, value: 0 };
    a.orders.push(r);
    if (r.jobId) a.jc += 1; else a.noJc += 1;
    if (!(r.jcPromiseDate || r.promiseDate)) a.noDod += 1;
    const left = balanceOf(r);
    a.left += left;
    a.value += left * num(r.rate ?? null);
    by.set(client, a);
  }
  // Most waiting for a job card first.
  const list = [...by.values()].sort((a, b) => b.noJc - a.noJc || b.orders.length - a.orders.length || a.client.localeCompare(b.client));
  const total = list.reduce((t, a) => ({
    orders: t.orders + a.orders.length, jc: t.jc + a.jc, noJc: t.noJc + a.noJc, noDod: t.noDod + a.noDod, left: t.left + a.left, value: t.value + a.value,
  }), { orders: 0, jc: 0, noJc: 0, noDod: 0, left: 0, value: 0 });
  const toggle = (c: string) => setOpen((s) => { const x = new Set(s); if (x.has(c)) x.delete(c); else x.add(c); return x; });
  const allOpen = list.length > 0 && list.every((a) => open.has(a.client));
  const num0 = (v: number, tone: string) => <span className={v > 0 ? `font-semibold ${tone}` : 'text-slate-300'}>{n(v)}</span>;

  return (
    <div className={`${maxHeight} overflow-y-auto font-sans`}>
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-[5] bg-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-[0_1px_0_#e2e8f0]">
          <tr>
            <th className="px-4 py-2">
              Client
              {list.length > 0 && (
                <button className="ml-2 normal-case tracking-normal text-brand hover:underline"
                        onClick={() => setOpen(allOpen ? new Set() : new Set(list.map((a) => a.client)))}>
                  {allOpen ? 'Close all' : 'Open all'}
                </button>
              )}
            </th>
            <th className="px-3 py-2 text-right">Orders</th>
            <th className="px-3 py-2 text-right">Job card raised</th>
            <th className="px-3 py-2 text-right">Awaiting job card</th>
            <th className="px-3 py-2 text-right">Awaiting DOD</th>
            <th className="px-3 py-2 text-right">Boxes left</th>
            <th className="px-3 py-2 text-right">Value left</th>
          </tr>
        </thead>
        <tbody>
          {list.map((a) => {
            const isOpen = open.has(a.client);
            // Waiting for a job card first, then the oldest PO.
            const orders = [...a.orders].sort((x, y) => (x.jobId ? 1 : 0) - (y.jobId ? 1 : 0)
              || String(x.poDate ?? '').localeCompare(String(y.poDate ?? '')));
            return (
              <Fragment key={a.client}>
                <tr className={`cursor-pointer border-t border-slate-100 ${isOpen ? 'bg-brand-50/60' : 'hover:bg-slate-50'}`}
                    onClick={() => toggle(a.client)} title={isOpen ? 'Hide its orders' : 'Show its orders'}>
                  <td className="px-4 py-2 font-medium text-slate-800">
                    <span className={`mr-1.5 inline-block w-3 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</span>
                    {a.client}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{num0(a.orders.length, 'text-slate-800')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num0(a.jc, 'text-emerald-700')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num0(a.noJc, 'text-brand')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num0(a.noDod, 'text-amber-700')}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{n(a.left)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">₹{money(a.value)}</td>
                </tr>
                {isOpen && (
                  <tr className="bg-slate-50/70">
                    <td colSpan={7} className="px-4 pb-3 pt-1">
                      <table className="w-full rounded border border-slate-200 bg-white text-xs">
                        <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-2 py-1.5">PO</th>
                            <th className="px-2 py-1.5">Product</th>
                            <th className="px-2 py-1.5">Type</th>
                            <th className="px-2 py-1.5">Job card</th>
                            <th className="px-2 py-1.5 text-right">Qty</th>
                            <th className="px-2 py-1.5 text-right">Left</th>
                            <th className="px-2 py-1.5">DOD</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((r) => {
                            const dod = r.jcPromiseDate || r.promiseDate;
                            return (
                              <tr key={r.poId} className="border-t border-slate-100 align-top">
                                <td className="px-2 py-1.5">
                                  {onOpenPo
                                    ? <button className="font-medium text-brand hover:underline" onClick={() => onOpenPo(r.poId)}>{r.poNumber || r.poId}</button>
                                    : <span className="font-medium text-slate-800">{r.poNumber || r.poId}</span>}
                                  {r.poDate && <div className="text-[10px] text-slate-400">{day(r.poDate)}</div>}
                                </td>
                                <td className="px-2 py-1.5 text-slate-700">{r.productName || r.jobName || '—'}</td>
                                <td className="px-2 py-1.5 text-slate-600">{r.orderType || '—'}</td>
                                <td className="px-2 py-1.5">
                                  {r.jobId
                                    ? <><span className="font-mono font-semibold text-emerald-700">{jcNo(r.jobId)}</span>
                                        {r.jcDate && <div className="text-[10px] text-slate-400">{day(r.jcDate)}</div>}</>
                                    : <span className="rounded bg-brand-50 px-1.5 py-0.5 font-medium text-brand">Awaiting job card</span>}
                                </td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{n(num(r.qty ?? null))}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{n(balanceOf(r))}</td>
                                <td className="px-2 py-1.5">{dod ? day(dod) : <span className="font-medium text-amber-700">No DOD</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {list.length === 0 && (
            <tr><td colSpan={7} className="p-8 text-center text-slate-500">No orders match these filters.</td></tr>
          )}
          {list.length > 0 && (
            <tr className="sticky bottom-0 border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <td className="px-4 py-2 text-slate-800">Total · {n(list.length)} clients</td>
              <td className="px-3 py-2 text-right tabular-nums">{n(total.orders)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{n(total.jc)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-brand">{n(total.noJc)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-amber-700">{n(total.noDod)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{n(total.left)}</td>
              <td className="px-3 py-2 text-right tabular-nums">₹{money(total.value)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
